import { CarHireLocation, CarHireLocationVendor } from './schemas/location.dto'

const CAR_HIRE_BRAND_NAMES: Record<string, string> = {
  '3': 'Avis',
  '4': 'Budget',
}

export type BrandedDetail<T> = {
  /** The brand this value belongs to, set only when the brands differ and the label is needed. */
  brandName?: string
  value: T
}

/**
 * Every car hire brand a location serves.
 *
 * Prefer the `vendors` list. The single `vendorBrandId` on a consolidated row carries one brand for
 * backwards compatibility only, so reading it would hide the other brand.
 */
export const getLocationBrandIds = (location?: CarHireLocation | null): string[] => {
  if (!location) {
    return []
  }

  const brandIds = location.vendors?.length
    ? location.vendors.map((vendor) => vendor.brandId)
    : [location.vendorBrandId]

  return Array.from(new Set(brandIds.filter((brandId): brandId is string => !!brandId)))
}

/**
 * The brands that can serve the whole trip. A return trip is served by the pick-up's own brands,
 * because upstream returns both brands whatever we ask for.
 */
/**
 * An upstream location id is a station code, not a place: AT5 names both Athens Downtown (GR) and
 * Atlanta/Tucker (US). A shared id only means the same place when the countries match too; with
 * either country unknown, the id comparison decides as before.
 */
export type CarHireLocationIdentity = Pick<CarHireLocation, 'id' | 'countryCode'>

export const isSameCarHirePlace = (
  pickUpLocation?: CarHireLocationIdentity | null,
  dropOffLocation?: CarHireLocationIdentity | null
): boolean => {
  if (!dropOffLocation || dropOffLocation.id !== pickUpLocation?.id) {
    return false
  }
  const pickUpCountry = pickUpLocation.countryCode
  const dropOffCountry = dropOffLocation.countryCode
  return !pickUpCountry || !dropOffCountry || pickUpCountry === dropOffCountry
}

export const getCommonBrandIds = (
  pickUpLocation?: CarHireLocation | null,
  dropOffLocation?: CarHireLocation | null
): string[] => {
  const pickUpBrandIds = getLocationBrandIds(pickUpLocation)
  if (!dropOffLocation || isSameCarHirePlace(pickUpLocation, dropOffLocation)) {
    return pickUpBrandIds
  }

  const dropOffBrandIds = getLocationBrandIds(dropOffLocation)
  return pickUpBrandIds.filter((brandId) => dropOffBrandIds.includes(brandId))
}

/**
 * The brand filter to send when searching for a drop-off.
 *
 * A pick-up that serves both brands filters by nothing: every location shares at least one brand
 * with it, so filtering would only hide bookable journeys.
 */
export const getDropOffBrandFilter = (
  pickUpLocation?: CarHireLocation | null
): string | undefined => {
  const pickUpBrandIds = getLocationBrandIds(pickUpLocation)
  return pickUpBrandIds.length === 1 ? pickUpBrandIds[0] : undefined
}

/**
 * True when no single brand can serve both ends of the trip, so it must be blocked before we search
 * for cars. Upstream answers that combination with a 500 whichever brand we send.
 *
 * A location whose brands we cannot tell is never blocked - we only block on evidence.
 */
export const hasNoBrandInCommon = (
  pickUpLocation?: CarHireLocation | null,
  dropOffLocation?: CarHireLocation | null
): boolean => {
  if (!pickUpLocation || !dropOffLocation) {
    return false
  }

  const pickUpBrandIds = getLocationBrandIds(pickUpLocation)
  const dropOffBrandIds = getLocationBrandIds(dropOffLocation)
  if (!pickUpBrandIds.length || !dropOffBrandIds.length) {
    return false
  }

  return getCommonBrandIds(pickUpLocation, dropOffLocation).length === 0
}

export const getCarHireBrandName = (brandId?: string): string | undefined =>
  brandId ? CAR_HIRE_BRAND_NAMES[brandId] : undefined

const hasValue = (value: unknown): boolean => {
  if (value == null) {
    return false
  }
  return Array.isArray(value) ? value.length > 0 : value !== ''
}

const isSameValue = <T>(first: T, second: T): boolean =>
  JSON.stringify(first) === JSON.stringify(second)

/**
 * A whole line that only restates the country, which one supplier includes and the other does not:
 * Edinburgh's Budget desk adds `Scotland`, Bristol's and Manchester's add `England`.
 *
 * Matched as a whole line rather than as words on purpose. Dropping the *word* `Northern` would take
 * it out of `Northern Boulevard` too, whereas a line reading exactly `Northern Ireland` is never
 * part of a street. Across the catalogue this removes `England`, `Scotland` and `Northern Ireland`
 * and nothing else.
 */
const REDUNDANT_REGION_LINES = new Set([
  'ENGLAND',
  'SCOTLAND',
  'WALES',
  'NORTHERNIRELAND',
  'UNITEDKINGDOM',
  'UK',
  'GB',
])

/**
 * The suppliers' own names, which locate nothing inside a block already headed by the brand: Malaga
 * publishes `Budget Aeropuerto De Malaga` against Avis's `Aeropuerto De Malaga`.
 *
 * Removing them cannot over-collapse, because a supplier's name is not what tells two desks apart:
 * Luton's brands both publish the same joint building, `Avis Budget Car Hire Centre`, and Miami's
 * both publish none at all while still differing on the suite. What separates addresses is handled
 * above, by {@link isSameAddress}.
 */
const BRAND_WORDS = new Set(['AVIS', 'BUDGET'])

const lettersAndDigits = (value: string): string => value.toUpperCase().replace(/[^A-Z0-9]+/g, '')

/**
 * An address reduced to what actually locates a desk.
 *
 * Case, punctuation and spacing are how the two suppliers spell the same place differently -
 * `O.R. Tambo` against `Or Tambo`, `Frankfurt am Main` against `Frankfurt Am Main`, `Niv-1` against
 * `Niv -1` - so ignoring them compares the address rather than the typing. A redundant country line
 * and the brand's own name go the same way.
 *
 * `dropEchoedWords` additionally removes a town that a line repeats while already standing as its
 * own line, which is Geneva's `Geneva Cointrin Airport` above the town line `Geneva`. It is a
 * separate step because it is the one that can be defeated by a *missing* space - see
 * {@link isSameAddress}.
 *
 * **Every step here is a function of one address alone**, and must stay that way. None of them asks
 * what the other brand published; they only remove what an address already states twice. Judging
 * two addresses against each other happens above, in {@link isSameAddress}, where the number guard
 * is there to make it safe.
 *
 * That split is the whole design. Plain similarity was tried and rejected, because the catalogue
 * holds a typo one character apart (Luton's `President` against `Presidents`) sitting next to two
 * genuinely different suites one character apart (Miami's `Ste 402` against `Ste 403`), and no
 * measure of distance tells them apart - the two desks are even the same 8 m and 10 m from each
 * other. What separates them is *which* characters differ, not how many. Showing one address twice
 * is untidy; merging two desks sends someone to the wrong counter with a flight to catch.
 */
const normalise = (address: string[] | null | undefined, dropEchoedWords: boolean): string => {
  const lines = (address ?? []).filter(
    (line) => !REDUNDANT_REGION_LINES.has(lettersAndDigits(line))
  )
  const wholeLines = new Set(lines.map(lettersAndDigits))

  return lines
    .map((line) => {
      const words = line
        .toUpperCase()
        .split(/[^A-Z0-9]+/)
        .filter(Boolean)
      // A single-word line is never stripped: it is the town or the postcode, and it is the thing
      // the repeat elsewhere is redundant *against*.
      const isRepeatable = words.length > 1
      return words
        .filter((word) => !BRAND_WORDS.has(word))
        .filter((word) => !(dropEchoedWords && isRepeatable && wholeLines.has(word)))
        .join('')
    })
    .join('')
}

export const normaliseAddress = (address?: string[] | null): string => normalise(address, true)

/**
 * Every number an address names, in no particular order.
 *
 * Read from the address as written rather than from the normalised form, because normalising joins
 * a line's words together and would fuse `LU2 9NL` into the single number 29.
 */
const addressNumbers = (address?: string[] | null): string[] =>
  (address ?? []).join(' ').match(/\d+/g) ?? []

const tally = (values: string[]): Map<string, number> =>
  values.reduce((counts, value) => counts.set(value, (counts.get(value) ?? 0) + 1), new Map())

const isSubTally = (part: Map<string, number>, whole: Map<string, number>): boolean =>
  Array.from(part.entries()).every(([value, count]) => count <= (whole.get(value) ?? 0))

/**
 * True when neither address names a number the other contradicts.
 *
 * **This is the guard that makes comparing the letters safe at all.** Numbers are how an address
 * locates: the street number, the suite, the floor, the terminal, the postcode. Every pair in the
 * catalogue that must never be merged differs in one, and differs in nothing else -
 * `Ste 402` against `Ste 403`, `2200` against `2300 Rental Car Center Pkwy`,
 * `3rd Floor` against `4th Floor` - so a rule that reads characters cannot tell them from a typo,
 * and a rule that reads numbers separates them without a threshold to tune.
 *
 * Extra numbers on one side are detail, not disagreement: one supplier prints the postcode and the
 * other does not. What is refused is a number that both sides name differently.
 */
const haveCompatibleNumbers = (first?: string[] | null, second?: string[] | null): boolean => {
  const firstNumbers = tally(addressNumbers(first))
  const secondNumbers = tally(addressNumbers(second))
  return isSubTally(firstNumbers, secondNumbers) || isSubTally(secondNumbers, firstNumbers)
}

/** True when `shorter` is `longer` with characters inserted - never with one swapped for another. */
const isSubsequence = (shorter: string, longer: string): boolean => {
  let cursor = 0
  return shorter.split('').every((character) => {
    cursor = longer.indexOf(character, cursor) + 1
    return cursor > 0
  })
}

/**
 * How much one supplier may spell out that the other abbreviates.
 *
 * Two covers what the catalogue actually varies by - `Av` against `Ave`, `Car Rent` against
 * `Car Rental`, `Dos Pinhais` against `D Pinhais`, `Antalya` against `Antlya`. Three would also
 * merge `Kruger Mpumalanga Airport` with `Kruger Mpumalanga Int Airport`, correctly, but it is
 * where the first uncomfortable case appears: Santa Ana, where one brand names a suite the other
 * omits entirely, and merging would print the address without it.
 */
const MAX_INSERTED_CHARACTERS = 2

/**
 * True when two addresses spell the same words, allowing for what one supplier writes out and the
 * other shortens.
 *
 * Insertions only. A character *swapped* for another is how `Terminal A` differs from `Terminal B`
 * and how Fort Lauderdale's `3rd Floor` differs from its `4th Floor`, so a substitution is always
 * treated as a real difference even when it is obviously a typo - Reno publishes `Apo` against
 * `Apt` and stays as two blocks. Erring towards two blocks costs a tidy modal; erring the other way
 * sends someone to the wrong counter.
 */
const haveMatchingLetters = (first?: string[] | null, second?: string[] | null): boolean =>
  [true, false].some((dropEchoedWords) => {
    const firstLetters = normalise(first, dropEchoedWords).replace(/\d+/g, '')
    const secondLetters = normalise(second, dropEchoedWords).replace(/\d+/g, '')
    if (firstLetters === secondLetters) {
      return true
    }
    const [shorter, longer] =
      firstLetters.length <= secondLetters.length
        ? [firstLetters, secondLetters]
        : [secondLetters, firstLetters]
    return (
      longer.length - shorter.length <= MAX_INSERTED_CHARACTERS && isSubsequence(shorter, longer)
    )
  })

/**
 * True when the two normalise to the same string outright.
 *
 * This runs ahead of the number guard rather than through it, because the guard reads numbers from
 * the address as written while normalising strips the spacing between them. Cardiff publishes
 * `CF62 3BD` against `CF623BD` - the same postcode, reaching the guard as `62` and `3` against
 * `623`, which reads as a contradiction. Two addresses that normalise identically are the same
 * address, and nothing downstream should be able to overrule that.
 */
const isSameNormalisedAddress = (first?: string[] | null, second?: string[] | null): boolean =>
  [true, false].some(
    (dropEchoedWords) => normalise(first, dropEchoedWords) === normalise(second, dropEchoedWords)
  )

/**
 * True only when two addresses are the same address, spelled differently. Never when merely alike.
 *
 * Numbers locate, letters spell. So the numbers must agree first - see
 * {@link haveCompatibleNumbers}, which is what refuses Miami's two suites and Denver's two street
 * numbers outright - and only then are the letters compared, generously enough to read past a
 * missing space, an abbreviation or a typo.
 *
 * The letters get two votes, with the echoed-town step on and off, and either is enough. That step
 * is a net gain but it needs the town to survive as a word, and one supplier's addresses are
 * missing a space in exactly the place that would leave it standing:
 *
 *     Avis    Car Rental Centre  Edinburgh Airport
 *     Budget  Car Rental CentreEdinburgh Airport
 *
 * `CentreEdinburgh` is one word, so `Edinburgh` is only dropped from Avis's side and the step
 * manufactures a difference out of a missing space. Comparing with it off recovers Edinburgh,
 * Bristol and Winnipeg.
 */
export const isSameAddress = (first?: string[] | null, second?: string[] | null): boolean =>
  isSameNormalisedAddress(first, second) ||
  (haveCompatibleNumbers(first, second) && haveMatchingLetters(first, second))

const digitsOnly = (value?: string | null): string => (value ?? '').replace(/[^0-9]/g, '')

/**
 * True when two brands publish the same telephone number, punctuated differently.
 *
 * The suppliers disagree about separators and about whether the country code carries a `+`:
 * `+32 2 7200944` against `32 2 7200944`, `+30 22860 33887` against `30-2286033887`. Comparing the
 * digits alone settles those without ever deciding that two *different* numbers are one.
 *
 * A `+` that the other side writes as `00` is deliberately left as a difference. It would need the
 * numbers' own country codes to resolve, and printing a number twice is cheaper than printing one
 * that does not dial.
 */
export const isSamePhoneNumber = (first?: string | null, second?: string | null): boolean => {
  const firstDigits = digitsOnly(first)
  const secondDigits = digitsOnly(second)
  // Nothing to compare digit-wise - an extension-only or free-text value falls back to the text.
  if (!firstDigits || !secondDigits) {
    return first === second
  }
  return firstDigits === secondDigits
}

/**
 * One entry per distinct value across a location's brands.
 *
 * Address and phone genuinely differ for roughly three in four airports - separate desks, separate
 * lines - so a merged row shows them per brand. When the brands agree, or only one publishes a
 * value, it shows once without a brand label.
 *
 * `isEquivalent` decides what "agree" means, and both callers pass one: addresses use
 * {@link isSameAddress} and phone numbers {@link isSamePhoneNumber}, so neither is printed twice
 * over a difference of punctuation. The exact default is only for values with no such spelling.
 */
export const getBrandedDetails = <T>(
  location: CarHireLocation | undefined | null,
  select: (vendor: CarHireLocationVendor) => T | undefined,
  fallback?: T,
  isEquivalent: (first: T, second: T) => boolean = isSameValue
): BrandedDetail<T>[] => {
  const published: BrandedDetail<T>[] = []
  ;(location?.vendors ?? []).forEach((vendor) => {
    const value = select(vendor)
    if (hasValue(value)) {
      published.push({ brandName: getCarHireBrandName(vendor.brandId), value: value as T })
    }
  })

  if (published.length === 0) {
    return hasValue(fallback) ? [{ value: fallback as T }] : []
  }
  if (published.every((detail) => isEquivalent(detail.value, published[0].value))) {
    // The first brand's spelling wins. Vendors arrive ordered by brand id, so the choice is Avis and
    // is stable between requests rather than following whatever order upstream answered in.
    return [{ value: published[0].value }]
  }
  return published
}

/** The address blocks the details panel will show, which the map has to stay in step with. */
export const getBrandedAddresses = (location?: CarHireLocation | null): BrandedDetail<string[]>[] =>
  getBrandedDetails(location, (vendor) => vendor.address, location?.address, isSameAddress)

/** Where one brand's desk is. */
export type BrandedCoordinate = {
  /** Set only when the location serves more than one brand, matching the address blocks. */
  brandName?: string
  latitude: number
  longitude: number
}

type Point = { latitude?: string; longitude?: string }

const toCoordinate = (
  points: Point[] | undefined,
  brandName?: string
): BrandedCoordinate | undefined => {
  const point = points?.[0]
  // `Number('')` is 0, so a blank coordinate would otherwise pin the Atlantic off Ghana rather than
  // showing no map at all.
  if (!point?.latitude?.trim() || !point?.longitude?.trim()) {
    return undefined
  }
  const latitude = Number(point.latitude)
  const longitude = Number(point.longitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return undefined
  }
  return { brandName, latitude, longitude }
}

/**
 * One point per brand that publishes one, in the same order as the address blocks.
 *
 * The location's own top-level coordinate is only ever the first brand's, so a merged row that maps
 * it alone silently shows the Avis desk and calls it the location. Reading the brands directly is
 * what lets the map say which desk each pin is.
 */
export const getBrandedCoordinates = (location?: CarHireLocation | null): BrandedCoordinate[] => {
  const coordinates = (location?.vendors ?? [])
    .map((vendor) => toCoordinate(vendor.location, getCarHireBrandName(vendor.brandId)))
    .filter((coordinate): coordinate is BrandedCoordinate => !!coordinate)

  if (coordinates.length > 1) {
    return coordinates
  }
  // One brand, or a brand that publishes no point of its own: there is nothing to tell apart, so
  // drop the label and fall back to the location's own coordinate.
  const single = coordinates[0] ?? toCoordinate(location?.location)
  return single ? [{ latitude: single.latitude, longitude: single.longitude }] : []
}

const EARTH_RADIUS_METRES = 6371000
const toRadians = (degrees: number): number => (degrees * Math.PI) / 180

/** Straight-line metres between two points. */
export const metresBetween = (first: BrandedCoordinate, second: BrandedCoordinate): number => {
  const deltaLatitude = toRadians(second.latitude - first.latitude)
  const deltaLongitude = toRadians(second.longitude - first.longitude)
  const haversine =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(toRadians(first.latitude)) *
      Math.cos(toRadians(second.latitude)) *
      Math.sin(deltaLongitude / 2) ** 2
  return 2 * EARTH_RADIUS_METRES * Math.asin(Math.sqrt(haversine))
}

/**
 * The raw gap between a location's two published points, or undefined when there is only one.
 *
 * This measures a gap; it never decides whether two addresses are the same place. That question has
 * no safe threshold - Bergamo's two desks share a coordinate exactly while Bristol's single desk
 * spans 649 m. For what is safe to show a customer, use
 * {@link getReportableDeskSeparationMetres}.
 */
export const getDeskSeparationMetres = (location?: CarHireLocation | null): number | undefined => {
  const coordinates = getBrandedCoordinates(location)
  return coordinates.length === 2 ? metresBetween(coordinates[0], coordinates[1]) : undefined
}

/**
 * Far enough apart that a customer arriving at the wrong desk would notice.
 *
 * This governs what the map draws and what the modal says about the gap. It never decides whether
 * two addresses describe one place - see {@link isSameAddress} for why no distance can.
 */
export const DISTINCT_DESK_METRES = 100

/**
 * Too far apart for both points to be describing desks at one location.
 *
 * Two hire desks at one airport are not 20 km apart; one of the two coordinates is simply wrong. The
 * catalogue has them - Bozeman's brands sit 23 km apart, Las Vegas's 21 km, Kaunas's 13 km, all
 * while publishing the same street address - and the upstream `en-GB` and `es-ES` points are far
 * worse than `en-US`, with sign-flipped and whole-degree-shifted longitudes. Above this we still
 * show the location, on its first brand's point, rather than drawing a second pin we have no reason
 * to believe and printing a distance nobody should walk.
 */
export const IMPLAUSIBLE_DESK_METRES = 2000

/**
 * The gap to draw and to tell the customer about, or undefined when there is nothing safe to say.
 *
 * The map and the sentence under it both read this, so they cannot disagree about whether this
 * location has one desk or two.
 */
export const getReportableDeskSeparationMetres = (
  location?: CarHireLocation | null
): number | undefined => {
  const metres = getDeskSeparationMetres(location)
  if (metres === undefined || metres <= DISTINCT_DESK_METRES) {
    return undefined
  }
  if (metres > IMPLAUSIBLE_DESK_METRES) {
    return undefined
  }
  // One address block means one desk, whatever the points say - and a second pin would carry a
  // label no address block claims. Where neither brand publishes an address there is no block to
  // contradict, so the points are left to speak for themselves.
  return getBrandedAddresses(location).length === 1 ? undefined : metres
}

export type MapPin = BrandedCoordinate & {
  /** The brand's initial, so a pin identifies its desk without a separate key. */
  label?: string
}

/**
 * What to draw on the map.
 *
 * Never more pins than there are address blocks beside them. Two pins need two desks that are far
 * enough apart to be worth telling apart, close enough together to believe, and named separately in
 * the text; anything else is one pin on the first brand's point.
 */
export const getMapPins = (location?: CarHireLocation | null): MapPin[] => {
  const coordinates = getBrandedCoordinates(location)
  if (coordinates.length !== 2 || getReportableDeskSeparationMetres(location) === undefined) {
    return coordinates.slice(0, 1).map(({ latitude, longitude }) => ({ latitude, longitude }))
  }

  const labels = coordinates.map((coordinate) => coordinate.brandName?.charAt(0).toUpperCase())
  // Initials that collide would label both pins the same, which is worse than labelling neither.
  const areDistinct = labels.every((label) => !!label) && new Set(labels).size === labels.length
  return coordinates.map((coordinate, index) => ({
    ...coordinate,
    label: areDistinct ? labels[index] : undefined,
  }))
}
